import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '@/lib/cache'
import { toCamelCase, toSnakeCase } from '@/lib/typeHelper'

// A customer's request to change their policies. The API and the console call these endorsements.

export enum PolicyChangeStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  NEEDS_INFO = 'NEEDS_INFO',
  WITH_CARRIER = 'WITH_CARRIER',
  QUOTE_READY = 'QUOTE_READY',
  ACCEPTED = 'ACCEPTED',
  BOUND = 'BOUND',
  POLICY_UPDATED = 'POLICY_UPDATED',
  COMPLETE = 'COMPLETE',
  DECLINED = 'DECLINED',
  CANCELED = 'CANCELED',
}

export enum PolicyChangeKind {
  ADD_TRUCK = 'ADD_TRUCK',
  REMOVE_TRUCK = 'REMOVE_TRUCK',
  ADD_TRAILER = 'ADD_TRAILER',
  REMOVE_TRAILER = 'REMOVE_TRAILER',
  CHANGE_VEHICLE = 'CHANGE_VEHICLE',
  OTHER_VEHICLE = 'OTHER_VEHICLE',
  ADD_DRIVER = 'ADD_DRIVER',
  REMOVE_DRIVER = 'REMOVE_DRIVER',
  CHANGE_DRIVER = 'CHANGE_DRIVER',
  OTHER_DRIVER = 'OTHER_DRIVER',
  CHANGE_LIMIT = 'CHANGE_LIMIT',
  CHANGE_DEDUCTIBLE = 'CHANGE_DEDUCTIBLE',
  ADD_COVERAGE = 'ADD_COVERAGE',
  REMOVE_COVERAGE = 'REMOVE_COVERAGE',
  CHANGE_OPERATIONS = 'CHANGE_OPERATIONS',
  OTHER_COVERAGE = 'OTHER_COVERAGE',
  ADD_ADDITIONAL_INSURED = 'ADD_ADDITIONAL_INSURED',
  ADD_LOSS_PAYEE = 'ADD_LOSS_PAYEE',
  WAIVER_OR_PRIMARY = 'WAIVER_OR_PRIMARY',
  REMOVE_INTEREST = 'REMOVE_INTEREST',
  CHANGE_NAMED_INSURED = 'CHANGE_NAMED_INSURED',
  CHANGE_ADDRESS = 'CHANGE_ADDRESS',
  CANCEL_OR_REINSTATE = 'CANCEL_OR_REINSTATE',
  OTHER = 'OTHER',
}

export enum RemovalReason {
  SOLD = 'SOLD',
  TOTAL_LOSS = 'TOTAL_LOSS',
  DECOMMISSIONED = 'DECOMMISSIONED',
  OTHER = 'OTHER',
}

export enum EffectiveBasis {
  REQUEST_DATE = 'REQUEST_DATE',
  CHOSEN = 'CHOSEN',
  TERM_LETTER = 'TERM_LETTER',
  LAST_LOAD = 'LAST_LOAD',
  ACTIVE_DATE = 'ACTIVE_DATE',
  FIRST_DRIVE = 'FIRST_DRIVE',
  BACKDATE_LIMIT = 'BACKDATE_LIMIT',
  QUOTED = 'QUOTED',
}

export enum PolicyChangeAction {
  EDIT = 'edit',
  SUBMIT = 'submit',
  /** Send back a request the broker asked the customer to change. */
  SEND_UPDATES = 'send_updates',
  /** Reply to what the broker asked, or send the document. */
  ANSWER = 'answer',
  ACCEPT = 'accept',
  DECLINE = 'decline',
  CANCEL = 'cancel',
  REOPEN = 'reopen',
}

/** One field an edit changes; field is the API's snake_case name. */
export interface PolicyChangeUnitEdit {
  field: string
  before: string | number | null
  after: string | number | null
}

export interface PolicyChangeUnit {
  /** The live unit's id, also for a removal's pending copy. */
  id: string
  type: 'truck' | 'trailer' | 'driver'
  status: string
  vin?: string
  year?: number | null
  make?: string
  model?: string
  vehicleNumber?: string | null
  price?: number | null
  firstName?: string
  lastName?: string
  licenseNumber?: string | null
  licenseState?: string | null
  dob?: string | null
  dateOfHire?: string | null
  /** Empty unless the change edits a unit on the policies. */
  edits: PolicyChangeUnitEdit[]
}

/** How a change reaches Cai's records once it's bound; the backend decides. */
export enum CaiUpdate {
  ROSTER = 'ROSTER',
  BUSINESS_NAME = 'BUSINESS_NAME',
  MAILING_ADDRESS = 'MAILING_ADDRESS',
  ROSTER_BY_HAND = 'ROSTER_BY_HAND',
  POLICY_DOCUMENT = 'POLICY_DOCUMENT',
  CERTIFICATE_HOLDERS = 'CERTIFICATE_HOLDERS',
  NOTHING = 'NOTHING',
}

export interface PolicyChangeLine {
  id: string
  kind: PolicyChangeKind
  unit: PolicyChangeUnit | null
  details: Record<string, string>
  description: string
  removalReason: RemovalReason | ''
  termLetterDate: string | null
  lastLoadDate: string | null
  startDate: string | null
  requestedDate: string | null
  effectiveDate: string | null
  effectiveBasis: EffectiveBasis | ''
  dropped: boolean
  dropReason: string
  caiUpdate: CaiUpdate
  /** What became of the change on each Corgi policy once the request was sent; staff only, else empty. */
  corgi?: CorgiChangeOutcome[]
}

export enum CorgiChangeResult {
  PLACED = 'placed',
  REFUSED = 'refused',
  /** The truck, trailer or driver is not on that Corgi policy, so there is nothing to change there. */
  NOT_ON_POLICY = 'not_on_policy',
  /** A kind of change Cai does not make on the portal. */
  UNSUPPORTED = 'unsupported',
}

export interface CorgiChangeOutcome {
  /** Empty for a change Cai makes on no Corgi policy at all. */
  policyNumber: string
  outcome: CorgiChangeResult
  /** Corgi's own words when it refused. */
  detail: string
}

export interface PolicyChangePolicy {
  id: string
  coverages: string[]
  carrierName: string
  policyNumber: string
  dateStart: string | null
  dateEnd: string | null
  /** Written through Corgi, so Cai drafts its endorsements on the portal when the request is sent on. */
  corgiEligible: boolean
}

export interface PolicyChangeQuote {
  id: string
  policyId: string
  submittedAt: string | null
  carrierReference: string
  proratedChange: string | null
  annualChange: string | null
  newTotalPremium: string | null
  effectiveDate: string | null
  expiresOn: string | null
  /** Whether the policy is written through Corgi, so Cai can draft its endorsements on the portal. */
  corgiEligible: boolean
  /** The drafts Cai made on Corgi for this policy's changes, once the request was sent there. */
  corgiDraft: CorgiDraft
}

export interface CorgiDraft {
  /** One per Corgi policy of the bind. */
  endorsements: CorgiEndorsement[]
  /** The request's changes no draft could take, for a person to add in the portal. */
  leftForStaff: string[]
  /** Policy-level remarks, such as an empty draft Cai cancelled. Optional for deploy skew. */
  notes?: string[]
}

/** What the quote keeps about one endorsement Cai drafted on Corgi. */
export interface CorgiEndorsement {
  policyNumber: string
  endorsementId: string
  status: string
  /** A change it carries was dropped from the request since, so its price no longer fits. */
  stale: boolean
  syncedAt: string | null
  portalUrl: string
  proratedChange: string | null
  annualChange: string | null
  newTotalPremium: string | null
}

export enum PolicyChangeAskKind {
  /** The request goes back to the customer to change. */
  CHANGE_REQUEST = 'CHANGE_REQUEST',
  /** The customer replies or sends a document; the request stays on its step. */
  REPLY = 'REPLY',
}

export interface PolicyChangeAskFile {
  id: string
  name: string
  url: string | null
}

/** Something the broker asked the customer, and what they sent back. */
export interface PolicyChangeAsk {
  id: string
  kind: PolicyChangeAskKind
  question: string
  needsDocument: boolean
  askedAt: string
  reply: string
  answeredAt: string | null
  open: boolean
  files: PolicyChangeAskFile[]
}

interface PolicyChangeHeader {
  id: string
  idShort: string
  status: PolicyChangeStatus
  source: 'PORTAL' | 'ROSTER' | 'BROKER' | 'ASK_CAI' | 'PHONE'
  /** Which of the eight steps it is on; a declined or canceled request keeps the step it stopped at. */
  step: number
  dateCreated: string
  submittedAt: string | null
  sentToCarrierAt: string | null
  quotesPostedAt: string | null
  decidedAt: string | null
  boundAt: string | null
  policyUpdatedAt: string | null
  completedAt: string | null
  declinedAt: string | null
  declineReason: string
  canceledAt: string | null
  question: string
}

export interface PolicyChangeRow extends PolicyChangeHeader {
  changeCounts: { trucks: number; trailers: number; drivers: number; other: number }
  earliestEffectiveDate: string | null
  proratedChangeTotal: string | null
  /** The broker is waiting on the customer: to answer, to change the request, or to decide on a quote. */
  waitingOnCustomer: boolean
}

export interface PolicyChangeRequest extends PolicyChangeHeader {
  policies: PolicyChangePolicy[]
  backdateLimitDays: number | null
  earliestEffectiveDate: string | null
  changes: PolicyChangeLine[]
  quotes: PolicyChangeQuote[]
  proratedChangeTotal: string | null
  annualChangeTotal: string | null
  /** Every posted quote is zero, as when staff approve a change with no price change. */
  priceUnchanged: boolean
  asks: PolicyChangeAsk[]
  /** What the broker asked that the customer hasn't answered yet. */
  openAsk: PolicyChangeAsk | null
  /** Whether the customer signed it; one the broker entered for them needs a signature when they first send it. */
  signed: boolean
  actions: PolicyChangeAction[]
}

/** The customer's reply to an ask: a note, files, or both. */
export interface PolicyChangeReply {
  reply: string
  files: File[]
}

function replyForm({ reply, files }: PolicyChangeReply, extra: Record<string, string> = {}): FormData {
  const form = new FormData()
  form.append('reply', reply)
  for (const file of files) form.append('files', file)
  for (const [key, value] of Object.entries(extra)) form.append(key, value)
  return form
}

/** What the customer sends for one change; unit carries a new truck, trailer or driver's fields. */
export interface PolicyChangeInput {
  kind?: PolicyChangeKind
  unit?: Record<string, unknown>
  truckId?: string
  trailerId?: string
  driverId?: string
  details?: Record<string, string>
  description?: string
  removalReason?: RemovalReason | ''
  termLetterDate?: string | null
  lastLoadDate?: string | null
  startDate?: string | null
  requestedDate?: string | null
}

const base = (orgId: string) => `/api/v1/insure/organisations/${orgId}/endorsements`

const asRequest = (responseBody: unknown) => toCamelCase(responseBody) as PolicyChangeRequest

export function usePolicyChanges(orgId: string | undefined) {
  const {
    data: requests,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [CacheKeys.POLICY_CHANGES, orgId, 'list'],
    queryFn: () => axios.get(`${base(orgId!)}/`).then((r) => toCamelCase(r.data) as PolicyChangeRow[]),
    enabled: !!orgId,
  })
  return { requests: requests ?? [], isLoading, isError }
}

export function usePolicyChange(orgId: string | undefined, requestId: string | undefined) {
  return useQuery({
    queryKey: [CacheKeys.POLICY_CHANGES, orgId, requestId],
    queryFn: () => axios.get(`${base(orgId!)}/${requestId}/`).then((r) => asRequest(r.data)),
    enabled: !!orgId && !!requestId,
  })
}

/** The open draft that Trucks, Trailers and Drivers page changes collect into; null when there is none. */
export function fetchRosterDraft(orgId: string): Promise<PolicyChangeRequest | null> {
  return axios
    .get(`${base(orgId)}/roster-draft/`)
    .then((r) => (r.status === 204 || !r.data ? null : asRequest(r.data)))
}

export function createPolicyChange(orgId: string): Promise<PolicyChangeRequest> {
  return axios.post(`${base(orgId)}/`, {}).then((r) => asRequest(r.data))
}

// Shared by every write, so the form can hold a withdrawal while a save is still in flight.
export const POLICY_CHANGE_WRITE_KEY = [CacheKeys.POLICY_CHANGES, 'write']

/**
 * Every write returns the whole request; it replaces the cached one and refreshes the list, and the
 * roster, whose pending rows the request's changes ride on.
 */
export function usePolicyChangeMutation<T>(
  orgId: string | undefined,
  send: (url: string, input: T) => Promise<{ data: unknown }>
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: POLICY_CHANGE_WRITE_KEY,
    mutationFn: (input: T) => send(base(orgId!), input).then((r) => asRequest(r.data)),
    onSuccess: async (request) => {
      // A read already in flight could finish after this write and put back what it replaced.
      await queryClient.cancelQueries({ queryKey: [CacheKeys.POLICY_CHANGES, orgId, request.id] })
      queryClient.setQueryData([CacheKeys.POLICY_CHANGES, orgId, request.id], request)
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.POLICY_CHANGES, orgId, 'list'] })
      for (const roster of [CacheKeys.TRUCKS, CacheKeys.TRAILERS, CacheKeys.DRIVERS]) {
        void queryClient.invalidateQueries({ queryKey: [roster, orgId] })
      }
    },
  })
}

export const policyChangeWrites = {
  addChange: (url: string, { requestId, change }: { requestId: string; change: PolicyChangeInput }) =>
    axios.post(`${url}/${requestId}/changes/`, toSnakeCase(change)),
  updateChange: (
    url: string,
    { requestId, changeId, change }: { requestId: string; changeId: string; change: PolicyChangeInput }
  ) => axios.patch(`${url}/${requestId}/changes/${changeId}/`, toSnakeCase(change)),
  removeChange: (url: string, { requestId, changeId }: { requestId: string; changeId: string }) =>
    axios.delete(`${url}/${requestId}/changes/${changeId}/`),
  submit: (url: string, { requestId, signature }: { requestId: string; signature: string }) =>
    axios.post(`${url}/${requestId}/submit/`, { signature }),
  // Staff send a request they built for the customer in the console; it carries no signature.
  submitForCustomer: (url: string, requestId: string) =>
    axios.post(`${url}/${requestId}/submit-for-customer/`),
  // The quotes as posted when the customer saw them, so a reposted price isn't accepted unseen.
  accept: (url: string, { requestId, quotesPostedAt }: { requestId: string; quotesPostedAt: string }) =>
    axios.post(`${url}/${requestId}/accept/`, { quotes_posted_at: quotesPostedAt }),
  sendUpdates: (
    url: string,
    {
      requestId,
      askId,
      signature,
      ...reply
    }: PolicyChangeReply & { requestId: string; askId: string | undefined; signature: string }
  ) =>
    axios.post(
      `${url}/${requestId}/send-updates/`,
      replyForm(reply, { signature, ...(askId ? { ask_id: askId } : {}) })
    ),
  // The ask the customer saw, so a reply never lands on a newer question.
  answer: (
    url: string,
    { requestId, askId, ...reply }: PolicyChangeReply & { requestId: string; askId: string }
  ) => axios.post(`${url}/${requestId}/answer/`, replyForm(reply, { ask_id: askId })),
  cancel: (url: string, requestId: string) => axios.post(`${url}/${requestId}/cancel/`),
  decline: (url: string, requestId: string) => axios.post(`${url}/${requestId}/decline/`),
  reopen: (url: string, requestId: string) => axios.post(`${url}/${requestId}/reopen/`),
}
